import type { CellContext, ColumnDef, Row } from '@tanstack/react-table';
import clsx from 'clsx';
import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { type JSX, useMemo } from 'react';
import { CollapsingTeamscaleLink, type CollapsingTeamscaleLinkProps } from 'ts/base/components/CollapsingTeamscaleLink';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { getTableRowSelectionColumn } from 'ts/base/table/GetTableRowSelectionColumn';
import { markdownValueAsTitleProp, valueAsTimestampTitleProp, valueAsTitleProp } from 'ts/base/table/TableUtils';
import { ArrayUtils } from 'ts/commons/ArrayUtils';
import { DateUtils } from 'ts/commons/DateUtils';
import type { Intersect } from 'ts/commons/Intersect';
import { Links } from 'ts/commons/links/Links';
import { Markdown } from 'ts/commons/markdown/Markdown';
import { MarkdownUtils } from 'ts/commons/markdown/MarkdownUtils';
import { openModal } from 'ts/commons/modal/ModalUtils';
import { PathUtils } from 'ts/commons/PathUtils';
import { StringUtils } from 'ts/commons/StringUtils';
import { getUserDisplayName, UserFullName } from 'ts/commons/UserFullName';
import { Icon } from 'ts/components/Icon';
import { ScrollArea } from 'ts/components/Modal';
import type { ExtendedTrackedFindingVoting } from 'ts/data/ExtendedTrackedFinding';
import type { FindingBasedSpecItemLinkProps } from 'ts/perspectives/findings/details/FindingBasedSpecItemLink';
import { FindingBasedSpecItemLink } from 'ts/perspectives/findings/details/FindingBasedSpecItemLink';
import { isPending } from 'ts/perspectives/findings/details/FlaggedFindingUtils';
import {
	FindingsUtils,
	type SpecItemFinding,
	type SpecItemFindingLocation
} from 'ts/perspectives/findings/FindingsUtils';
import { AssessmentIcon } from 'ts/perspectives/findings/list/AssessmentIcon';
import { attachContextMenuHandler } from 'ts/perspectives/findings/list/ContextMenu';
import type { FindingContextMenuHandler } from 'ts/perspectives/findings/list/FindingContextMenu';
import { COLUMN_LABEL_BY_ID } from 'ts/perspectives/findings/list/FindingsTable';
import { FindingTaskSelectionDialog } from 'ts/perspectives/quality_control/tasks/dialog/FindingTaskSelectionDialog';
import {
	TASK_STATUS_ICONS,
	TASK_STATUS_TOOLTIPS,
	TaskUtils
} from 'ts/perspectives/quality_control/tasks/utils/TaskUtils';
import { EFindingBlacklistType } from 'typedefs/EFindingBlacklistType';
import { ETaskStatus } from 'typedefs/ETaskStatus';
import { ETrafficLightColor, type ETrafficLightColorEntry } from 'typedefs/ETrafficLightColor';
import type { ExtendedTrackedFinding } from 'typedefs/ExtendedTrackedFinding';
import type { FindingBlacklistInfo } from 'typedefs/FindingBlacklistInfo';
import type { SimpleTask } from 'typedefs/SimpleTask';
import type { SpecItem } from 'typedefs/SpecItem';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import styles from './FindingsTable.module.less';

function getPopupTextFromAssessment(assessmentDisplayName: string): string {
	if (assessmentDisplayName === 'YELLOW') {
		return 'Yellow';
	}
	if (assessmentDisplayName === 'RED') {
		return 'Red';
	}
	return '';
}

function getColoredBordersFromAssessmentColor(assessment: ETrafficLightColorEntry | undefined) {
	return {
		className: clsx({
			[styles.borderRed!]: assessment === ETrafficLightColor.RED.name,
			[styles.borderYellow!]: assessment === ETrafficLightColor.YELLOW.name
		})
	};
}

/** Returns the column definitions for the assessment icon column. */
function getAssessmentFindingColumn<Finding extends ExtendedTrackedFindingVoting>(): ColumnDef<Finding> {
	return {
		header: '',
		id: 'assessment',
		accessorKey: 'assessment',
		cell({ row }: CellContext<Finding, unknown>) {
			return (
				// The <div> is required, otherwise the popup won't render.
				// See: https://github.com/Semantic-Org/Semantic-UI-React/issues/1413
				<div
					title={getPopupTextFromAssessment(row.original.assessment ?? ETrafficLightColor.UNKNOWN.name)}
					data-testid="assessment-popup"
				>
					<AssessmentIcon finding={row.original} />
				</div>
			);
		},
		enableResizing: false,
		size: 30,
		sizeUnit: 'px',
		meta: {
			csv: {
				accessor: data => data.assessment ?? '',
				header: 'Assessment'
			}
		}
	};
}

/**
 * Returns the column definitions for all columns that are shared between the findings list view and all other findings
 * tables in Teamscale (e.g. delta perspective, commit details, merge request details, ...).
 */
function useCommonFindingsTableColumns<Finding extends ExtendedTrackedFindingVoting>(
	includeFindingCheckboxes: boolean,
	commit: UnresolvedCommitDescriptor | null,
	isSolvedIconShown: boolean,
	project: string,
	uniformPath: string,
	searchParamsId: string | undefined,
	properties: string[] | undefined,
	referencedSpecItems: Record<string, SpecItem>,
	linkTarget?: string
): Array<ColumnDef<Finding>> {
	return useMemo(() => {
		return [
			...(includeFindingCheckboxes
				? [
						getTableRowSelectionColumn<Finding>({
							getCellProps({ row }) {
								return getColoredBordersFromAssessmentColor(row.original.assessment);
							},
							className: styles.findingAssessmentBorder
						})
					]
				: []),
			getAssessmentFindingColumn<Finding>(),
			{
				header: 'Message',
				accessorKey: 'message',
				minSize: 30,
				size: 2,
				sortingFn: sortMessages,
				cell({ row }: CellContext<Finding, unknown>) {
					return (
						<FindingMessageCell
							commit={commit}
							row={row}
							isSolvedIconShown={isSolvedIconShown}
							project={project}
							searchParamsId={searchParamsId}
							linkTarget={linkTarget}
						/>
					);
				},
				meta: {
					className: styles.resizableTableMessageColumn,
					...(includeFindingCheckboxes
						? {}
						: {
								getCellProps({ row }) {
									return getColoredBordersFromAssessmentColor(row.original.assessment);
								}
							}),
					csv: {
						accessor: data => MarkdownUtils.renderToPlainText(data.message),
						header: 'Message'
					}
				}
			},
			{
				header: () => <TextHeader text="Location" />,
				id: 'uniformPath',
				sortingFn: sortCodeLocations,
				accessorFn(finding: Finding) {
					return PathUtils.getPathWithRawLineLocation(finding.location);
				},
				cell({ row }: CellContext<Finding, unknown>) {
					const finding = row.original;
					let specItem: SpecItem | undefined;
					if ('issueId' in finding.location) {
						const issueId = referencedSpecItems[finding.location.issueId];
						specItem = referencedSpecItems[issueId?.id.internalId ?? ''];
					}

					if (specItem) {
						return (
							<SpecItemFindingLocationLink
								project={project}
								relativeToUniformPath={uniformPath}
								commit={finding.commit ?? commit}
								linkTarget={linkTarget}
								specItem={specItem}
								location={finding.location as SpecItemFindingLocation}
							/>
						);
					}
					return (
						<FindingLocationLink
							project={project}
							relativeToUniformPath={uniformPath}
							location={finding.location}
							commit={finding.commit ?? commit}
							linkTarget={linkTarget}
						/>
					);
				},
				size: 2,
				meta: {
					className: styles.pathColumn,
					csv: {
						accessor: data => PathUtils.getPathWithRawLineLocation(data.location, uniformPath),
						header: 'Location'
					}
				}
			},
			{
				header: () => <TextHeader text="Category" />,
				id: 'group',
				accessorFn(finding: Finding) {
					return `${finding.categoryName} › ${finding.groupName} › ${finding.typeName}`;
				},
				size: 2,
				meta: {
					getCellProps: markdownValueAsTitleProp,
					csv: {
						accessor: data => `${data.categoryName} › ${data.groupName} › ${data.typeName}`,
						header: 'Category'
					}
				},
				cell(cell) {
					return MarkdownUtils.renderToPlainText(cell.getValue() as string);
				}
			},
			...FindingsUtils.filterRelevantFindingProperties(properties ?? []).map(
				property =>
					({
						header: () => (
							<TextHeader
								text={COLUMN_LABEL_BY_ID.get(property) ?? FindingsUtils.sanitizePropertyId(property)}
							/>
						),
						id: property,

						accessorFn(finding: Finding) {
							if (
								(property === 'Toleration Reporter' || property === 'False Positive Reporter') &&
								finding.blacklistInfo?.resolvedUser
							) {
								const user = finding.blacklistInfo.resolvedUser;
								return getUserDisplayName(user.username, user);
							}
							return finding.properties[property];
						},

						cell(cell: CellContext<Finding, unknown>) {
							const value = cell.getValue();
							if (value) {
								if (property === 'Toleration Date' || property === 'False Positive Date') {
									return DateUtils.formatTimestamp(value as string);
								}

								const finding = cell.row.original;
								if (
									(property === 'Toleration Reporter' || property === 'False Positive Reporter') &&
									finding.blacklistInfo?.resolvedUser
								) {
									const user = finding.blacklistInfo.resolvedUser;
									return <UserFullName username={user.username} user={user} />;
								}
								if (property === 'Check') {
									return MarkdownUtils.renderToPlainText(value as string);
								}
								return value;
							}
							return null;
						},

						meta: {
							className: clsx({
								// Other finding properties should be shown exactly as given in the finding (except that line breaks must be ignored, this is a rather long table and line breaks would look strange)
								// We need to enforce (use !) since this would be overwritten with whitespace-nowrap otherwise.
								'!whitespace-pre': property !== 'Toleration Date' && property !== 'False Positive Date'
							}),
							getCellProps:
								property === 'Toleration Date' || property === 'False Positive Date'
									? valueAsTimestampTitleProp
									: property === 'Check'
										? markdownValueAsTitleProp
										: valueAsTitleProp,
							csv: {
								accessor: data => {
									if (
										(property === 'Toleration Reporter' ||
											property === 'False Positive Reporter') &&
										data.blacklistInfo?.resolvedUser
									) {
										const user = data.blacklistInfo.resolvedUser;
										return getUserDisplayName(user.username, user);
									}
									const value = data.properties[property];
									if (property === 'Toleration Date' || property === 'False Positive Date') {
										return value ? DateUtils.format(new Date(value as string), 'YYYY-MM-DD') : '';
									}
									if (property === 'Check') {
										return value ? MarkdownUtils.renderToPlainText(value as string) : '';
									}
									return value ?? '';
								},
								header: COLUMN_LABEL_BY_ID.get(property) ?? FindingsUtils.sanitizePropertyId(property)
							}
						}
					}) as ColumnDef<Finding>
			)
		];
	}, [
		includeFindingCheckboxes,
		properties,
		commit,
		isSolvedIconShown,
		project,
		searchParamsId,
		linkTarget,
		uniformPath,
		referencedSpecItems
	]);
}

/**
 * Provides the column definitions that are used for the findings list view. This includes the finding's "introduction
 * date" and the tasks associated with it compared to useCommonFindingsTableColumns.
 */
export function useFindingsTableColumnDefinition(
	commit: UnresolvedCommitDescriptor | null,
	isSolvedIconShown: boolean,
	project: string,
	uniformPath: string,
	searchParamsId: string | undefined,
	properties: string[] | undefined,
	contextMenuHandler: FindingContextMenuHandler | undefined,
	referencedSpecItems: Record<string, SpecItem>,
	linkTarget?: string
): Array<ColumnDef<ExtendedTrackedFinding>> {
	const columnDefinition = useCommonFindingsTableColumns<ExtendedTrackedFinding>(
		true,
		commit,
		isSolvedIconShown,
		project,
		uniformPath,
		searchParamsId,
		properties,
		referencedSpecItems,
		linkTarget
	);
	return useMemo(() => {
		const columns: Array<ColumnDef<ExtendedTrackedFinding>> = [
			...columnDefinition,
			{
				header: () => <TextHeader text="Introduction Date" />,
				id: 'birth',
				size: 155,
				sizeUnit: 'px',
				minSize: 40,
				accessorFn(finding: ExtendedTrackedFinding) {
					return finding.birth.timestamp;
				},
				cell({ row }: CellContext<ExtendedTrackedFinding, unknown>) {
					const finding = row.original;
					return DateUtils.formatTimestamp(finding.birth.timestamp);
				},
				meta: {
					getCellProps({ row }) {
						const finding = row.original;
						return {
							title: DateUtils.formatTimestamp(finding.birth.timestamp)
						};
					},
					alignment: 'right',
					csv: {
						accessor: data => DateUtils.format(new Date(data.birth.timestamp), 'YYYY-MM-DD'),
						header: 'Introduction Date'
					}
				}
			},
			{
				header: () => <TextHeader text="Tasks" />,
				id: 'tasks',
				enableResizing: true,
				size: 55,
				sizeUnit: 'px',
				accessorFn(finding: ExtendedTrackedFinding) {
					return Object.values(finding.tasksByStatus).flat().length;
				},
				cell({ row }: CellContext<ExtendedTrackedFinding, unknown>) {
					const finding = row.original;
					return <ShowTasks project={project} finding={finding} />;
				},
				meta: {
					className: 'single line',
					csv: {
						accessor: data => String(Object.values(data.tasksByStatus).flat().length),
						header: 'Tasks'
					}
				}
			} as ColumnDef<ExtendedTrackedFinding>
		];
		if (!contextMenuHandler) {
			return columns;
		}
		return attachContextMenuHandler(columns, contextMenuHandler);
	}, [columnDefinition, contextMenuHandler, project]);
}

type FindingMessageCellProps<Finding extends ExtendedTrackedFindingVoting> = {
	row: Row<Finding>;
	commit: UnresolvedCommitDescriptor | null;
	project: string;
	isSolvedIconShown: boolean;
	searchParamsId?: string;
	linkTarget?: string;
};

function FindingMessageCell<Finding extends ExtendedTrackedFindingVoting>({
	row,
	commit,
	project,
	isSolvedIconShown,
	searchParamsId,
	linkTarget
}: FindingMessageCellProps<Finding>) {
	const finding = row.original;
	const detailsLinkCommit = determineLinkCommit(finding, commit);
	return (
		<>
			<BlacklistIcon blacklistInfo={finding.blacklistInfo} />
			<VoteExcludedIcon finding={finding} />
			<FindingSolvedIcon project={project} finding={finding} isSolvedIconShown={isSolvedIconShown} />
			<TeamscaleLink
				data-testid="finding-message-link"
				to={Links.findingDetails(project, finding.id, {
					searchParamsId,
					commit: detailsLinkCommit
				})}
				target={linkTarget}
				title={MarkdownUtils.renderToPlainText(finding.message)}
			>
				<FindingMessage finding={finding} />
			</TeamscaleLink>
		</>
	);
}

/**
 * Returns the commit for the given finding, that can be used to create a link. Spec item findings can have different
 * commits as they come from synchronizer branches.
 */
function determineLinkCommit(finding: ExtendedTrackedFindingVoting, commit: UnresolvedCommitDescriptor | null) {
	if (FindingsUtils.isSpecItemFindingLocation(finding.location)) {
		// spec item commits lie on their respective connector branch
		return new UnresolvedCommitDescriptor(commit?.getTimestamp(), finding.birth.branchName);
	}

	return (
		(finding.death
			? UnresolvedCommitDescriptor.getPreviousCommit(UnresolvedCommitDescriptor.wrap(finding.death))
			: finding.commit) ?? commit
	);
}

type FindingMessageProps = { finding: ExtendedTrackedFinding };

function FindingMessage({ finding }: FindingMessageProps) {
	const ignoredFindingStyle = isVoteExcludedFinding(finding) ? 'text-zinc-400' : '';
	return <Markdown inline className={ignoredFindingStyle} markdownText={finding.message} />;
}

/** Sorts code locations of findings in findings table. */
export function sortCodeLocations<Finding extends TrackedFinding>(rowA: Row<Finding>, rowB: Row<Finding>): number {
	const locationA = rowA.original.location;
	const locationB = rowB.original.location;
	const locationOrder = ArrayUtils.defaultCompare(locationA.uniformPath, locationB.uniformPath);
	if (locationOrder !== 0) {
		return locationOrder;
	}
	const startLineA = PathUtils.isTextRegionLocation(locationA) ? locationA.rawStartLine : 0;
	const startLineB = PathUtils.isTextRegionLocation(locationB) ? locationB.rawStartLine : 0;
	const startOrder = ArrayUtils.defaultCompare(startLineA, startLineB);
	if (startOrder !== 0) {
		return startOrder;
	}
	const endLineA = PathUtils.isTextRegionLocation(locationA) ? locationA.rawEndLine : 0;
	const endLineB = PathUtils.isTextRegionLocation(locationB) ? locationB.rawEndLine : 0;
	const endOrder = ArrayUtils.defaultCompare(endLineA, endLineB);
	if (endOrder !== 0) {
		return endOrder;
	}
	return 0;
}

/**
 * Sorts the message column of the findings in the findings table, also taking into account the code locations when
 * there are multiple equal messages.
 */
function sortMessages<Finding extends TrackedFinding>(rowA: Row<Finding>, rowB: Row<Finding>): number {
	const messageA = rowA.original.message;
	const messageB = rowB.original.message;

	const messageComparisonResult = messageA.localeCompare(messageB);

	if (messageComparisonResult !== 0) {
		return messageComparisonResult;
	}
	return sortCodeLocations(rowA, rowB);
}

type BlacklistIconProps = {
	blacklistInfo?: FindingBlacklistInfo | null;
};

/** Shows the toleration or false positive icon. */
export function BlacklistIcon({ blacklistInfo }: BlacklistIconProps): JSX.Element | null {
	if (blacklistInfo == null) {
		return null;
	}
	const isToleration = blacklistInfo.type === EFindingBlacklistType.TOLERATION.name;
	const title =
		(isToleration ? 'Tolerated' : 'Marked as False Positive') +
		' by ' +
		blacklistInfo.user +
		' on ' +
		DateUtils.formatTimestamp(blacklistInfo.timestamp) +
		'. Rationale: ' +
		blacklistInfo.rationale;
	return (
		<span title={title}>
			{isPending(blacklistInfo.approvalState) ? (
				<Icon data-testid="pending-icon" name="pending exclusion" />
			) : null}
			<Icon
				className="color-red"
				data-testid="flagging-icon"
				name={isToleration ? 'toleration' : 'false positive'}
			/>
		</span>
	);
}

/**
 * Icon appears when the finding is excluded from voting on the CCP due to include/exclude patterns specified in the
 * connector configs, the helps to indicate the difference in findings between CCP and TS.
 */
function VoteExcludedIcon({ finding }: { finding: ExtendedTrackedFindingVoting }) {
	if (isVoteExcludedFinding(finding)) {
		const formatPattern = (pattern: string, prefix: string) =>
			!StringUtils.isEmptyOrWhitespace(pattern) ? ' [' + prefix + pattern + ']' : '';
		const { includes, excludes } = finding.voteIncludeExcludePattern!;
		const patterns = formatPattern(includes, 'Includes: ') + formatPattern(excludes, 'Excluded: ');
		const title = 'This finding was excluded from voting due to connector include/exclude patterns:' + patterns;
		return <Icon title={title} color="grey" name="bell slash" />;
	}
	return null;
}

function isVoteExcludedFinding(finding: ExtendedTrackedFindingVoting) {
	const pattern = finding.voteIncludeExcludePattern;
	return pattern && !pattern.isIncluded(finding.location.uniformPath);
}

type TextHeaderProps = { text: string };

function TextHeader({ text }: TextHeaderProps) {
	return <span title={text}>{text}</span>;
}

type FindingSolvedIconProps<Finding extends TrackedFinding> = {
	finding: Finding;
	project: string;
	isSolvedIconShown: boolean;
};

function FindingSolvedIcon<Finding extends TrackedFinding>({
	finding,
	project,
	isSolvedIconShown
}: FindingSolvedIconProps<Finding>): JSX.Element | null {
	if (isSolvedIconShown && finding.death != null) {
		const title = `Solved in ${finding.death.branchName} branch on ${DateUtils.formatTimestamp(
			finding.death.timestamp
		)}`;
		return (
			<TeamscaleLink className="icon-solved" title={title} to={Links.commitDetails(project, finding.death)}>
				<Icon name="check" />
			</TeamscaleLink>
		);
	}
	return null;
}

type FindingLocationLinkProps = {
	project: string;
	/** Makes the uniform path of the location relative to given sub path. */
	relativeToUniformPath: string;
	commit: UnresolvedCommitDescriptor | null;
	location: TrackedFinding['location'];
	linkTarget?: string;
};

/** A link to a given finding location. */
export function FindingLocationLink({
	location,
	project,
	relativeToUniformPath,
	commit,
	linkTarget
}: FindingLocationLinkProps): JSX.Element | null {
	const link = PathUtils.getLinkToFileLocation(project, location, commit);
	const readableLocation = PathUtils.getPathWithRawLineLocation(location, relativeToUniformPath);
	return <CollapsingTeamscaleLink to={link} uniformPath={readableLocation} {...getDefaultLinkProps(linkTarget)} />;
}

type SpecItemFindingLocationLinkProps = FindingLocationLinkProps & {
	specItem: SpecItem;
	location: SpecItemFinding['location'];
};

/** A link to a given finding location. */
function SpecItemFindingLocationLink({
	location,
	project,
	commit,
	linkTarget,
	specItem
}: SpecItemFindingLocationLinkProps): JSX.Element | null {
	return (
		<FindingBasedSpecItemLink
			projectId={project}
			commit={commit ?? undefined}
			findingLocation={location}
			specItem={specItem}
			{...getDefaultLinkProps(linkTarget)}
		/>
	);
}

function getDefaultLinkProps(linkTarget: string | undefined) {
	const defaultLinkProps: Partial<Intersect<FindingBasedSpecItemLinkProps, CollapsingTeamscaleLinkProps>> & {
		'data-testid'?: string;
	} = {
		addCopyToClipboardButton: 'onHover',
		target: linkTarget,
		'data-testid': 'finding-location-link'
	};
	return defaultLinkProps;
}

type ShowTasksProps = {
	project: string;
	finding: ExtendedTrackedFindingVoting;
};

/** The task summary for a given finding. */
function ShowTasks({ project, finding }: ShowTasksProps): JSX.Element | null {
	const tasks = finding.tasksByStatus;
	return (
		<>
			{ETaskStatus.values.map(status => {
				return (
					<FindingTaskSummaryItem
						key={status.name}
						tooltipText={TASK_STATUS_TOOLTIPS[status.name]}
						icon={TASK_STATUS_ICONS[status.name]}
						project={project}
						taskStatus={status}
						tasks={tasks[status.name] ?? []}
					/>
				);
			})}
		</>
	);
}

type FindingTaskSummaryItemProps = {
	tooltipText: string;
	icon: JSX.Element;
	taskStatus: ETaskStatus;
	tasks: SimpleTask[];
	project: string;
};

/** A list item for the task summary */
function FindingTaskSummaryItem({
	tooltipText,
	icon,
	taskStatus,
	tasks,
	project
}: FindingTaskSummaryItemProps): JSX.Element | null {
	if (tasks.length === 0) {
		return null;
	}

	function onClickLinkedTasksSummaryLink(): void {
		openModal({
			title: 'Associated Tasks: ' + taskStatus.readableName,
			scrollAreaComponent: ScrollArea.Autosize,
			contentRenderer: () => (
				<FindingTaskSelectionDialog
					tasks={tasks}
					projectId={project}
					onSelectTask={taskId => TaskUtils.navigateToTaskDetailView(project, taskId)}
				/>
			)
		});
	}

	return (
		<button
			type="button"
			className="item linked-tasks-summary teamscale-link-button"
			data-status={taskStatus.name}
			title={tooltipText}
			onClick={onClickLinkedTasksSummaryLink}
		>
			{icon}
			{tasks.length}
		</button>
	);
}
